import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  selectIsAuthenticated,
  selectIsAdmin,
  selectIsInternal,
  selectIsEventManager,
  selectAuthInitialized,
  selectAuthLoading,
} from '../store/authSlice';

interface ProtectedRouteProps {
  /** The route content to render when access is granted */
  children: React.ReactNode;
  /** When true, only admin users may access this route */
  adminOnly?: boolean;
  /** When true, only internal users may access this route */
  internalOnly?: boolean;
  /** When true, only admins OR event-managers may access this route */
  eventManagerOrAdmin?: boolean;
}

/**
 * Wraps a route to enforce authentication (and optionally admin-only access).
 *
 * Behaviour:
 * - While the auth session is still being restored, renders a loading spinner.
 * - Unauthenticated users are redirected to /login (with the intended path saved
 *   in location state so LoginPage can redirect back after sign-in).
 * - Non-admin users attempting to access an adminOnly route are redirected to /media.
 * - Non-internal users attempting to access an internalOnly route are redirected to /media.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  adminOnly = false,
  internalOnly = false,
  eventManagerOrAdmin = false,
}) => {
  const initialized = useSelector(selectAuthInitialized);
  const loading = useSelector(selectAuthLoading);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isAdmin = useSelector(selectIsAdmin);
  const isInternal = useSelector(selectIsInternal);
  const isEventManager = useSelector(selectIsEventManager);
  const location = useLocation();

  // Wait until the initial auth check has completed
  if (!initialized || loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Preserve the attempted URL so we can redirect back after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/media" replace />;
  }

  if (internalOnly && !isInternal) {
    return <Navigate to="/media" replace />;
  }

  if (eventManagerOrAdmin && !isAdmin && !isEventManager) {
    return <Navigate to="/media" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
